import { useState, useEffect } from "react";
import axios from "axios";
import { Plus, Trash2, Play, FileText, Clock, Mail, Calendar, Download } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { downloadFile, downloadDirectExcel } from "../lib/download";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AVAILABLE_METRICS = [
  { key: "total_hours", label: "Total Hours", category: "Time" },
  { key: "billable_hours", label: "Billable Hours", category: "Time" },
  { key: "non_billable_hours", label: "Non-Billable Hours", category: "Time" },
  { key: "utilization_rate", label: "Utilization Rate %", category: "Performance" },
  { key: "consultant_count", label: "Consultant Count", category: "Resources" },
  { key: "project_count", label: "Project Count", category: "Projects" },
  { key: "entries_count", label: "Entries Count", category: "Data" },
];

export default function ReportBuilder() {
  const { isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState("custom");
  const [reports, setReports] = useState([]);
  const [scheduledReports, setScheduledReports] = useState([]);
  const [reportResult, setReportResult] = useState(null);
  const [runningReport, setRunningReport] = useState(null);

  // New report form
  const [newReport, setNewReport] = useState({
    name: "", description: "", metrics: ["all"],
    filters: { start_date: "", end_date: "", projects: "", consultants: "" },
    layout: "table", is_public: false,
  });
  const [createOpen, setCreateOpen] = useState(false);

  // Scheduled report form
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [newSchedule, setNewSchedule] = useState({
    enabled: true, frequency: "weekly", day_of_week: 1, day_of_month: 1,
    recipients: "", report_type: "executive_summary", include_pdf: true,
  });

  const fetchReports = async () => {
    try {
      const [customRes, schedRes] = await Promise.all([
        axios.get(`${API}/custom-reports`),
        isAdmin ? axios.get(`${API}/admin/scheduled-reports`).catch(() => ({ data: { reports: [] } })) : Promise.resolve({ data: { reports: [] } }),
      ]);
      setReports(customRes.data.reports || []);
      setScheduledReports(schedRes.data.reports || []);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { fetchReports(); }, []);

  const createReport = async () => {
    if (!newReport.name.trim()) { toast.error("Report name required"); return; }
    try {
      await axios.post(`${API}/custom-reports`, newReport);
      toast.success("Report created");
      setCreateOpen(false);
      setNewReport({ name: "", description: "", metrics: ["all"], filters: { start_date: "", end_date: "" }, layout: "table", is_public: false });
      fetchReports();
    } catch (e) { toast.error("Failed to create report"); }
  };

  const runReport = async (reportId) => {
    setRunningReport(reportId);
    try {
      const res = await axios.post(`${API}/custom-reports/${reportId}/run`);
      setReportResult({ ...res.data, _report_id: reportId });
      toast.success("Report generated");
    } catch (e) { toast.error("Failed to run report"); }
    finally { setRunningReport(null); }
  };

  const deleteReport = async (reportId) => {
    try {
      await axios.delete(`${API}/custom-reports/${reportId}`);
      toast.success("Report deleted");
      fetchReports();
    } catch (e) { toast.error("Failed to delete"); }
  };

  const exportReportExcel = async (reportId, reportName, filters) => {
    try {
      if (reportId) {
        await downloadFile(`${API}/export/excel/report/${reportId}`);
      } else {
        await downloadDirectExcel(reportName || "Report", filters?.start_date, filters?.end_date);
      }
      toast.success("Excel downloaded");
    } catch (e) {
      console.error("Export error:", e);
      toast.error("Export failed");
    }
  };

  const createSchedule = async () => {
    try {
      const payload = {
        ...newSchedule,
        recipients: newSchedule.recipients.split(",").map(e => e.trim()).filter(Boolean),
      };
      await axios.post(`${API}/admin/scheduled-reports`, payload);
      toast.success("Schedule created");
      setScheduleOpen(false);
      fetchReports();
    } catch (e) { toast.error("Failed to create schedule"); }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="report-builder">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">Report Builder</h1>
        <p className="text-muted-foreground mt-1">Create custom reports and schedule automated email reports</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="custom" className="gap-2"><FileText className="w-4 h-4" />Custom Reports</TabsTrigger>
          {isAdmin && <TabsTrigger value="scheduled" className="gap-2"><Clock className="w-4 h-4" />Scheduled Reports</TabsTrigger>}
        </TabsList>

        {/* CUSTOM REPORTS */}
        <TabsContent value="custom" className="space-y-6">
          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">{reports.length} saved reports</p>
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button className="gap-2"><Plus className="w-4 h-4" /> New Report</Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>Create Custom Report</DialogTitle></DialogHeader>
                <div className="space-y-4 py-2">
                  <div className="space-y-2"><Label>Report Name</Label><Input value={newReport.name} onChange={(e) => setNewReport(p => ({ ...p, name: e.target.value }))} placeholder="e.g., Q1 Utilization Summary" /></div>
                  <div className="space-y-2"><Label>Description</Label><Input value={newReport.description} onChange={(e) => setNewReport(p => ({ ...p, description: e.target.value }))} placeholder="Optional description" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2"><Label>Start Date</Label><Input type="date" value={newReport.filters.start_date} onChange={(e) => setNewReport(p => ({ ...p, filters: { ...p.filters, start_date: e.target.value } }))} /></div>
                    <div className="space-y-2"><Label>End Date</Label><Input type="date" value={newReport.filters.end_date} onChange={(e) => setNewReport(p => ({ ...p, filters: { ...p.filters, end_date: e.target.value } }))} /></div>
                  </div>
                  <div className="space-y-2">
                    <Label>Project Numbers (comma separated, leave blank for all)</Label>
                    <Input value={newReport.filters.projects} onChange={(e) => setNewReport(p => ({ ...p, filters: { ...p.filters, projects: e.target.value } }))} placeholder="e.g., 556, 521, 436" />
                  </div>
                  <div className="space-y-2">
                    <Label>Consultants (comma separated, leave blank for all)</Label>
                    <Input value={newReport.filters.consultants} onChange={(e) => setNewReport(p => ({ ...p, filters: { ...p.filters, consultants: e.target.value } }))} placeholder="e.g., Duncan Bryson, Rachel Davis" />
                  </div>
                  <div className="space-y-2">
                    <Label>Metrics</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {AVAILABLE_METRICS.map(m => (
                        <label key={m.key} className="flex items-center gap-2 text-sm">
                          <input type="checkbox" checked={newReport.metrics.includes("all") || newReport.metrics.includes(m.key)}
                            onChange={(e) => {
                              if (e.target.checked) setNewReport(p => ({ ...p, metrics: [...p.metrics.filter(x => x !== "all"), m.key] }));
                              else setNewReport(p => ({ ...p, metrics: p.metrics.filter(x => x !== m.key) }));
                            }} />
                          {m.label}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={newReport.is_public} onCheckedChange={(c) => setNewReport(p => ({ ...p, is_public: c }))} />
                    <Label>Share with team</Label>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
                    <Button onClick={createReport}>Create Report</Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {reports.map(r => (
              <Card key={r.id} className="hover:shadow-md transition-[box-shadow] duration-200">
                <CardContent className="pt-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold">{r.name}</h3>
                      {r.description && <p className="text-sm text-muted-foreground mt-1">{r.description}</p>}
                      <div className="flex gap-2 mt-2">
                        {r.is_public && <Badge variant="secondary" className="text-xs">Shared</Badge>}
                        <Badge variant="secondary" className="text-xs">{r.layout}</Badge>
                        {r.filters?.start_date && <Badge variant="outline" className="text-xs">{r.filters.start_date} - {r.filters.end_date}</Badge>}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="outline" size="sm" className="gap-1" onClick={() => runReport(r.id)} disabled={runningReport === r.id}>
                        <Play className="w-3 h-3" /> {runningReport === r.id ? "Running..." : "Run"}
                      </Button>
                      <Button variant="outline" size="sm" className="gap-1" onClick={() => exportReportExcel(r.id, r.name, r.filters)}>
                        <Download className="w-3 h-3" /> Excel
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => deleteReport(r.id)}>
                        <Trash2 className="w-3 h-3 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {reports.length === 0 && (
              <Card className="border-dashed col-span-2"><CardContent className="flex flex-col items-center justify-center py-12">
                <FileText className="w-10 h-10 text-muted-foreground mb-3" />
                <p className="font-medium">No custom reports yet</p>
                <p className="text-sm text-muted-foreground">Click "New Report" to create your first custom report</p>
              </CardContent></Card>
            )}
          </div>

          {/* Report Result */}
          {reportResult && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base font-semibold">{reportResult.report_name} - Results</CardTitle>
                    <CardDescription>Filters: {JSON.stringify(reportResult.filters_applied)}</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" className="gap-1.5" onClick={() => exportReportExcel(reportResult._report_id, reportResult.report_name, reportResult.filters_applied)}>
                      <Download className="w-3.5 h-3.5" /> Export Excel
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setReportResult(null)}>Close</Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                  {Object.entries(reportResult.data || {}).map(([key, val]) => (
                    <div key={key} className="p-3 rounded-lg bg-muted/50">
                      <p className="text-xs text-muted-foreground capitalize">{key.replace(/_/g, " ")}</p>
                      <p className="text-lg font-bold tabular-nums">{typeof val === "number" && key.includes("rate") ? `${val}%` : val}</p>
                    </div>
                  ))}
                </div>
                {(reportResult.by_consultant || []).length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="border-b border-border">
                        <th className="text-left py-2 px-3 text-xs font-semibold uppercase text-muted-foreground">Consultant</th>
                        <th className="text-right py-2 px-3 text-xs font-semibold uppercase text-muted-foreground">Total</th>
                        <th className="text-right py-2 px-3 text-xs font-semibold uppercase text-muted-foreground">Billable</th>
                        <th className="text-right py-2 px-3 text-xs font-semibold uppercase text-muted-foreground">Non-Billable</th>
                        <th className="text-right py-2 px-3 text-xs font-semibold uppercase text-muted-foreground">Utilization</th>
                      </tr></thead>
                      <tbody>{reportResult.by_consultant.map(c => (
                        <tr key={c.personnel} className="border-b border-border/50">
                          <td className="py-2 px-3 font-medium">{c.personnel}</td>
                          <td className="py-2 px-3 text-right tabular-nums">{c.total_hours}h</td>
                          <td className="py-2 px-3 text-right tabular-nums text-emerald-600">{c.fee_hours}h</td>
                          <td className="py-2 px-3 text-right tabular-nums text-amber-600">{c.non_fee_hours}h</td>
                          <td className="py-2 px-3 text-right tabular-nums">{c.utilization}%</td>
                        </tr>
                      ))}</tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* SCHEDULED REPORTS */}
        {isAdmin && (
          <TabsContent value="scheduled" className="space-y-6">
            <div className="flex justify-between items-center">
              <p className="text-sm text-muted-foreground">{scheduledReports.length} scheduled reports</p>
              <Dialog open={scheduleOpen} onOpenChange={setScheduleOpen}>
                <DialogTrigger asChild>
                  <Button className="gap-2"><Plus className="w-4 h-4" /> New Schedule</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Schedule Automated Report</DialogTitle></DialogHeader>
                  <div className="space-y-4 py-2">
                    <div className="space-y-2">
                      <Label>Report Type</Label>
                      <Select value={newSchedule.report_type} onValueChange={(v) => setNewSchedule(p => ({ ...p, report_type: v }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="executive_summary">Executive Summary</SelectItem>
                          <SelectItem value="utilization">Utilization Report</SelectItem>
                          <SelectItem value="profitability">Profitability Report</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Frequency</Label>
                      <Select value={newSchedule.frequency} onValueChange={(v) => setNewSchedule(p => ({ ...p, frequency: v }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Recipients (comma separated emails)</Label>
                      <Input value={newSchedule.recipients} onChange={(e) => setNewSchedule(p => ({ ...p, recipients: e.target.value }))} placeholder="director@company.com, admin@company.com" />
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch checked={newSchedule.include_pdf} onCheckedChange={(c) => setNewSchedule(p => ({ ...p, include_pdf: c }))} />
                      <Label>Include PDF attachment</Label>
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" onClick={() => setScheduleOpen(false)}>Cancel</Button>
                      <Button onClick={createSchedule}>Create Schedule</Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <div className="space-y-3">
              {scheduledReports.map(r => (
                <Card key={r.id}>
                  <CardContent className="pt-5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-lg ${r.enabled ? "bg-emerald-500/10" : "bg-muted"}`}>
                          <Mail className={`w-5 h-5 ${r.enabled ? "text-emerald-500" : "text-muted-foreground"}`} />
                        </div>
                        <div>
                          <p className="font-medium capitalize">{r.report_type?.replace(/_/g, " ")}</p>
                          <p className="text-sm text-muted-foreground">{r.frequency} · {(r.recipients || []).join(", ") || "No recipients"}</p>
                          <div className="flex gap-2 mt-1">
                            <Badge variant={r.enabled ? "secondary" : "outline"} className="text-xs">{r.enabled ? "Active" : "Paused"}</Badge>
                            {r.include_pdf && <Badge variant="outline" className="text-xs">PDF</Badge>}
                            {r.last_sent && <Badge variant="outline" className="text-xs">Last: {new Date(r.last_sent).toLocaleDateString()}</Badge>}
                          </div>
                        </div>
                      </div>
                      <Button variant="ghost" size="icon" onClick={async () => {
                        try { await axios.delete(`${API}/admin/scheduled-reports/${r.id}`); toast.success("Deleted"); fetchReports(); }
                        catch { toast.error("Failed"); }
                      }}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {scheduledReports.length === 0 && (
                <Card className="border-dashed"><CardContent className="flex flex-col items-center justify-center py-12">
                  <Calendar className="w-10 h-10 text-muted-foreground mb-3" />
                  <p className="font-medium">No scheduled reports</p>
                  <p className="text-sm text-muted-foreground">Create a schedule to receive automated email reports</p>
                </CardContent></Card>
              )}
            </div>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
